import { memo, useCallback, useState, type FormEvent } from 'react'
import { LogOut, UserRound } from 'lucide-react'
import { authApi } from '../../lib/authApi.js'
import { useAuthUser, useStore } from '../../store/index.js'
import { ModalShell } from '../common/ModalShell.js'
import { Button } from '../ui/button.js'

const ROLE_LABELS = {
  ADMIN: 'Administrateur',
  JOURNALIST: 'Journaliste',
  EDITOR: 'Monteur',
  TECHNICIAN: 'Technicien',
  ADS_INFOSERVICE: 'Secrétariat',
} as const

export const AccountMenu = memo(function AccountMenu() {
  const user = useAuthUser()
  const [open, setOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [name, setName] = useState(user?.name ?? '')
  const [email, setEmail] = useState(user?.email ?? '')
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [totp, setTotp] = useState('')
  const [codes, setCodes] = useState<string[] | null>(null)
  const [message, setMessage] = useState('')

  const refresh = useCallback(async () => useStore.getState().setAuth(await authApi.me()), [])
  const logout = useCallback(async () => {
    await authApi.logout().catch(() => {})
    useStore.getState().clearAuth()
  }, [])
  const saveProfile = useCallback(async (event: FormEvent) => {
    event.preventDefault(); setMessage('')
    try {
      await authApi.updateProfile(name, email)
      await refresh()
      setMessage('Profil enregistré.')
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Erreur')
    }
  }, [email, name, refresh])
  const savePassword = useCallback(async (event: FormEvent) => {
    event.preventDefault(); setMessage('')
    try {
      await authApi.changePassword(currentPassword, newPassword)
      await refresh()
      setCurrentPassword(''); setNewPassword('')
      setMessage('Mot de passe modifié.')
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Erreur')
    }
  }, [currentPassword, newPassword, refresh])
  const forgetDevices = useCallback(async () => {
    setMessage('')
    try {
      const { count } = await authApi.forgetTrustedDevices()
      setMessage(count === 0
        ? 'Aucun appareil de confiance enregistré.'
        : `${count} appareil${count > 1 ? 's' : ''} oublié${count > 1 ? 's' : ''} : le code TOTP sera redemandé à la prochaine connexion.`)
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Erreur')
    }
  }, [])
  const regenerate = useCallback(async () => {
    setMessage('')
    try {
      setCodes((await authApi.regenerateRecoveryCodes(currentPassword, totp)).recoveryCodes)
      setTotp('')
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Erreur')
    }
  }, [currentPassword, totp])

  if (!user) return null
  return (
    <div className="tp-account">
      <button className="tp-account__trigger" type="button" onClick={() => setOpen(value => !value)} aria-expanded={open}>
        <UserRound size={15} />
        <span>{user.name}</span>
      </button>
      {open && (
        <div className="tp-account__menu">
          <strong>{user.name}</strong>
          <span>{ROLE_LABELS[user.role]} · @{user.username}</span>
          <button type="button" onClick={() => { setOpen(false); setProfileOpen(true) }}>Mon compte</button>
          <button type="button" onClick={() => void logout()}><LogOut size={14} /> Déconnexion</button>
        </div>
      )}
      {profileOpen && (
        <ModalShell title="Mon compte" size="medium" onClose={() => { setProfileOpen(false); setCodes(null); setMessage('') }}>
          <div className="tp-account-modal">
            <form onSubmit={saveProfile}>
              <h3>Profil</h3>
              <label>Nom affiché<input className="tp-input" value={name} onChange={event => setName(event.target.value)} /></label>
              <label>E-mail<input className="tp-input" type="email" value={email} onChange={event => setEmail(event.target.value)} /></label>
              <Button type="submit">Enregistrer le profil</Button>
            </form>
            <form onSubmit={savePassword}>
              <h3>Mot de passe</h3>
              <label>Mot de passe actuel<input className="tp-input" type="password" value={currentPassword} onChange={event => setCurrentPassword(event.target.value)} /></label>
              <label>Nouveau mot de passe<input className="tp-input" type="password" minLength={12} maxLength={128} value={newPassword} onChange={event => setNewPassword(event.target.value)} /></label>
              <Button type="submit">Changer le mot de passe</Button>
            </form>
            {user.role === 'ADMIN' && (
              <section>
                <h3>Codes de récupération</h3>
                <p>Confirmez le mot de passe actuel et un code TOTP.</p>
                <label>Code TOTP<input className="tp-input" value={totp} onChange={event => setTotp(event.target.value)} /></label>
                <Button type="button" variant="outline" onClick={() => void regenerate()}>Régénérer les codes</Button>
                {codes && <div className="auth-recovery-codes">{codes.map(code => <code key={code}>{code}</code>)}</div>}
              </section>
            )}
            {user.role === 'ADMIN' && (
              <section>
                <h3>Appareils de confiance</h3>
                <p>Les appareils mémorisés à la saisie du code TOTP n’ont plus à le fournir. Changer de mot de passe les oublie aussi.</p>
                <Button type="button" variant="outline" onClick={() => void forgetDevices()}>Oublier tous mes appareils</Button>
              </section>
            )}
            {message && <p role="status">{message}</p>}
          </div>
        </ModalShell>
      )}
    </div>
  )
})

